import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { forgotPassword } from "@/requests/gen/react-query/auth";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import type { JSX } from "react";

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPasswordPage,
});

interface ResetPasswordForm {
  email: string;
}

/**
 * ForgotPasswordPage - Where the user inputs an email to get a password reset email
 * @returns {JSX.Element} The ForgotPasswordPage component
 */
function ForgotPasswordPage(): JSX.Element {
  const form = useForm<ResetPasswordForm>({
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(formData: ResetPasswordForm) {
    try {
      await forgotPassword({ email: formData.email });
    } catch {
      // Intentionally left blank to protect from email enumeration
    }
  }

  return (
    <div className="flex w-full max-w-sm flex-col gap-6">
      <title>Reset Password - Legrande Health Portal</title>
      <Card className="sm:max-w-96 w-full text-center">
        <CardHeader>
          <CardTitle>Reset Password</CardTitle>
          <CardDescription>Enter your email address</CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="text-left space-y-2"
            >
              <FormField
                control={form.control}
                rules={{ required: "Email is required" }}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="text" autoComplete="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="pt-4 flex flex-col gap-4">
                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                >
                  Send Password Reset Email
                </Button>
                {form.formState.isSubmitted && (
                  <p className="text-muted-foreground">
                    If the email address is valid, you will receive a password
                    reset email shortly.
                  </p>
                )}
                <Button
                  type="button"
                  variant="link"
                  disabled={form.formState.isSubmitting}
                  className="w-full"
                  asChild
                >
                  <Link to={"/auth/login"}>
                    <ChevronLeft className="size-4" /> Back to Sign In
                  </Link>
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
